import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Users, Briefcase, FileText, MessageSquare, Shield, Megaphone, BarChart3, AlertCircle, TrendingUp, UserX, Activity, ChevronDown, ChevronUp } from 'lucide-react';

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllUsers, setShowAllUsers] = useState(false);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await api.get('/admin/analytics');
        setAnalytics(response.data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <DashboardLayout title="Admin Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a1a2e]"></div>
        </div>
      </DashboardLayout>
    );
  }

  const stats = [
    { label: 'Total Users', value: analytics?.users?.total || 0, icon: Users, color: '#6366f1', sub: `${analytics?.users?.total_researchers || 0} Researchers / ${analytics?.users?.total_clients || 0} Clients`, link: '/admin/moderation' },
    { label: 'Profiles', value: analytics?.profiles?.total || 0, icon: Users, color: '#FF6B35', sub: `${analytics?.profiles?.deactivated || 0} deactivated`, link: '/admin/moderation' },
    { label: 'Opportunities', value: analytics?.opportunities?.total || 0, icon: Briefcase, color: '#0D7377', sub: `${analytics?.opportunities?.open || 0} open / ${analytics?.opportunities?.deactivated || 0} deactivated`, link: '/admin/moderation' },
    { label: 'Briefs', value: analytics?.briefs?.total || 0, icon: Megaphone, color: '#8b5cf6', sub: `${analytics?.briefs?.open || 0} open / ${analytics?.briefs?.deactivated || 0} deactivated`, link: '/admin/moderation' },
    { label: 'Applications', value: analytics?.applications?.total || 0, icon: FileText, color: '#059669', sub: `${analytics?.applications?.pending || 0} pending`, link: '/admin/moderation' },
    { label: 'Messages', value: analytics?.messages?.total || 0, icon: MessageSquare, color: '#0ea5e9', sub: '', link: null },
  ];

  const funnel = analytics?.funnel || {};
  const dailySignups = analytics?.daily_signups || [];
  const usersNoProfile = analytics?.users_no_profile || [];
  const maxSignup = Math.max(...dailySignups.map(d => d.count), 1);

  const funnelRate = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="space-y-6">
        {/* Alert: Pending items */}
        {(analytics?.verifications?.pending > 0) && (
          <Link to="/admin/verifications" className="block bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-3 hover:bg-amber-100 transition-colors cursor-pointer" data-testid="admin-pending-alert">
            <AlertCircle size={20} className="text-amber-600" />
            <span className="text-amber-800 text-sm font-medium">
              {analytics.verifications.pending} verification request(s) pending review
            </span>
          </Link>
        )}

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-stats-grid">
          {stats.map((stat) => {
            const Icon = stat.icon;
            const Card = stat.link ? Link : 'div';
            const cardProps = stat.link ? { to: stat.link } : {};
            return (
              <Card key={stat.label} {...cardProps} className={`bg-white border border-neutral-200 p-5 ${stat.link ? 'hover:border-neutral-400 transition-colors cursor-pointer' : ''}`} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: stat.color + '15' }}>
                    <Icon size={20} style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">{stat.label}</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{stat.value}</p>
                  </div>
                </div>
                {stat.sub && <p className="text-xs text-neutral-400">{stat.sub}</p>}
              </Card>
            );
          })}
        </div>

        {/* Signup & Funnel Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Signups */}
          <div className="bg-white border border-neutral-200 p-6" data-testid="recent-signups-card">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <TrendingUp size={20} className="text-indigo-500" />
              Recent Signups
            </h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-indigo-50 border border-indigo-100 p-4 text-center">
                <p className="text-3xl font-bold text-indigo-700" style={{ fontFamily: 'Outfit' }}>{funnel.signups_7d || 0}</p>
                <p className="text-xs text-indigo-500 mt-1">Last 7 days</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-100 p-4 text-center">
                <p className="text-3xl font-bold text-indigo-700" style={{ fontFamily: 'Outfit' }}>{funnel.signups_30d || 0}</p>
                <p className="text-xs text-indigo-500 mt-1">Last 30 days</p>
              </div>
            </div>
          </div>

          {/* User Funnel */}
          <div className="bg-white border border-neutral-200 p-6" data-testid="user-funnel-card">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Activity size={20} className="text-emerald-500" />
              User Funnel
            </h2>
            <div className="space-y-3">
              {/* Registered */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-neutral-600">Registered Users</span>
                  <span className="font-semibold">{funnel.total_users || 0}</span>
                </div>
                <div className="h-3 bg-neutral-100 overflow-hidden">
                  <div className="h-full bg-indigo-500" style={{ width: '100%' }}></div>
                </div>
              </div>
              {/* Created Profile */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-neutral-600">Created a Profile</span>
                  <span className="font-semibold">{funnel.users_with_profiles || 0} <span className="text-neutral-400 font-normal">({funnelRate(funnel.users_with_profiles, funnel.total_users)}%)</span></span>
                </div>
                <div className="h-3 bg-neutral-100 overflow-hidden">
                  <div className="h-full bg-orange-400" style={{ width: `${funnelRate(funnel.users_with_profiles, funnel.total_users)}%` }}></div>
                </div>
              </div>
              {/* Completed Profile */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-neutral-600">Completed Profile (has headline)</span>
                  <span className="font-semibold">{funnel.profiles_completed || 0} <span className="text-neutral-400 font-normal">({funnelRate(funnel.profiles_completed, funnel.total_users)}%)</span></span>
                </div>
                <div className="h-3 bg-neutral-100 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${funnelRate(funnel.profiles_completed, funnel.total_users)}%` }}></div>
                </div>
              </div>
              {/* Drop-off highlight */}
              {funnel.users_without_profiles > 0 && (
                <div className="mt-2 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
                  <UserX size={16} />
                  <span><strong>{funnel.users_without_profiles}</strong> user(s) registered but never created a profile</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Daily Signups Chart */}
        {dailySignups.length > 0 && (
          <div className="bg-white border border-neutral-200 p-6" data-testid="daily-signups-chart">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <BarChart3 size={20} className="text-blue-500" />
              Signups (Last 30 Days)
            </h2>
            <div className="flex items-end gap-[3px] h-40">
              {dailySignups.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                  <div
                    className="w-full bg-indigo-400 hover:bg-indigo-600 transition-colors rounded-t-sm min-h-[2px]"
                    style={{ height: `${Math.max((d.count / maxSignup) * 100, 2)}%` }}
                  ></div>
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-neutral-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                    {d.date}: {d.count} signup{d.count !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-neutral-400">
              <span>{dailySignups[0]?.date}</span>
              <span>{dailySignups[Math.floor(dailySignups.length / 2)]?.date}</span>
              <span>{dailySignups[dailySignups.length - 1]?.date}</span>
            </div>
          </div>
        )}

        {/* Users Without Profiles Table */}
        {usersNoProfile.length > 0 && (
          <div className="bg-white border border-neutral-200 p-6" data-testid="users-no-profile-table">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <UserX size={20} className="text-amber-500" />
              Users Without Profiles ({usersNoProfile.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 text-left">
                    <th className="pb-2 font-medium text-neutral-500">Name</th>
                    <th className="pb-2 font-medium text-neutral-500">Email</th>
                    <th className="pb-2 font-medium text-neutral-500">Role</th>
                    <th className="pb-2 font-medium text-neutral-500">Auth</th>
                    <th className="pb-2 font-medium text-neutral-500">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllUsers ? usersNoProfile : usersNoProfile.slice(0, 10)).map((u) => (
                    <tr key={u.user_id} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="py-2.5 font-medium text-neutral-800">{u.name || '-'}</td>
                      <td className="py-2.5 text-neutral-600">{u.email}</td>
                      <td className="py-2.5">
                        <span className={`text-xs font-medium px-2 py-0.5 ${u.role === 'researcher' ? 'bg-orange-50 text-orange-600' : 'bg-teal-50 text-teal-600'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="py-2.5 text-neutral-500 capitalize">{u.auth_method || '-'}</td>
                      <td className="py-2.5 text-neutral-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {usersNoProfile.length > 10 && (
              <button
                onClick={() => setShowAllUsers(!showAllUsers)}
                className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                data-testid="toggle-users-list"
              >
                {showAllUsers ? <><ChevronUp size={14} /> Show less</> : <><ChevronDown size={14} /> Show all {usersNoProfile.length} users</>}
              </button>
            )}
          </div>
        )}

        {/* Platform Summary */}
        <div className="bg-white border border-neutral-200 p-6">
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <BarChart3 size={20} />
            Platform Summary
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-neutral-700 mb-2">Content Status</h3>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li className="flex justify-between"><span>Active Opportunities</span><span className="font-medium">{(analytics?.opportunities?.total || 0) - (analytics?.opportunities?.deactivated || 0)}</span></li>
                <li className="flex justify-between"><span>Active Briefs</span><span className="font-medium">{(analytics?.briefs?.total || 0) - (analytics?.briefs?.deactivated || 0)}</span></li>
                <li className="flex justify-between"><span>Active Profiles</span><span className="font-medium">{(analytics?.profiles?.total || 0) - (analytics?.profiles?.deactivated || 0)}</span></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-medium text-neutral-700 mb-2">Moderation Queue</h3>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li className="flex justify-between">
                  <span>Pending Verifications</span>
                  <span className={`font-medium ${analytics?.verifications?.pending > 0 ? 'text-amber-600' : 'text-neutral-600'}`}>
                    {analytics?.verifications?.pending || 0}
                  </span>
                </li>
                <li className="flex justify-between"><span>Pending Applications</span><span className="font-medium">{analytics?.applications?.pending || 0}</span></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
